import { useState, useEffect, useCallback } from 'react';
import { Download, FileText, BookOpen, ChevronDown, ChevronRight, Printer, Link2, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const GRADE_COLORS = {
  "K": "bg-pink-50 border-pink-200 text-pink-700",
  "1": "bg-orange-50 border-orange-200 text-orange-700",
  "2": "bg-amber-50 border-amber-200 text-amber-700",
  "3-4": "bg-emerald-50 border-emerald-200 text-emerald-700",
  "5-6": "bg-cyan-50 border-cyan-200 text-cyan-700",
  "7-8": "bg-blue-50 border-blue-200 text-blue-700",
  "9-12": "bg-violet-50 border-violet-200 text-violet-700",
};

const MODALITY_ICONS = {
  "Listening": "🎧",
  "Reading": "📖",
  "Speaking": "🗣",
  "Writing": "✏️",
};

/**
 * Trigger a file download using a short-lived download token.
 * The token lets the browser fetch the file natively (no blob, no memory issues).
 * Handles 12-25MB PDF files reliably across all browsers.
 */
async function triggerDownload(url) {
  const token = localStorage.getItem('mosaic_session_token');
  if (!token) throw new Error('Not logged in');

  // Step 1: Get a signed download URL from the backend
  const filename = url.split('/').pop();
  const tokenRes = await fetch(`${API}/api/blm/download-token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ filename }),
  });

  if (!tokenRes.ok) {
    throw new Error(`Token request failed: ${tokenRes.status}`);
  }

  const { download_url } = await tokenRes.json();

  // Step 2: Open the signed URL — browser handles the download natively
  const fullDownloadUrl = `${API}${download_url}`;
  const a = document.createElement('a');
  a.href = fullDownloadUrl;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  // Cleanup
  setTimeout(() => document.body.removeChild(a), 500);
}

function DownloadButton({ url, label, icon: Icon, colorClasses, testId }) {
  const [downloading, setDownloading] = useState(false);

  const handleClick = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!url || downloading) return;

    setDownloading(true);
    try {
      await triggerDownload(url);
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Download failed', {
        description: 'Please try again in a moment.',
      });
    } finally {
      // Keep spinner for a bit so user knows download initiated
      setTimeout(() => setDownloading(false), 2000);
    }
  }, [url, downloading]);

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={downloading || !url}
      className={`inline-flex items-center gap-1 cursor-pointer ${colorClasses} ${downloading ? 'opacity-60 cursor-wait' : ''} ${!url ? 'opacity-40 cursor-not-allowed' : ''}`}
      data-testid={testId}
    >
      {downloading ? <Loader2 size={12} className="animate-spin" /> : <Icon size={12} />}
      {downloading ? 'Preparing...' : label}
    </button>
  );
}

/**
 * Build the correct filename for a BLM PDF.
 * Pattern: MOSAIC_{grade}_{modality}_{bookletType}_{tier}[_FormA|_FormB].pdf
 * Grade is kept as-is (K, 1, 2, 3-4, 5-6, 7-8, 9-12) — matches the
 * hyphenated files produced by scripts/regenerate_blm_pdfs.py. Historical
 * versions replaced '-' with '_', which caused multi-grade-band downloads
 * to trigger slow on-demand regeneration and time out.
 */
function blmFilename(grade, modality, bookletType, tier, form = null) {
  const base = `MOSAIC_${grade}_${modality}_${bookletType}_${tier}`;
  return form ? `${base}_${form}.pdf` : `${base}.pdf`;
}

function buildUrl(grade, modality, bookletType, tier, form = null) {
  // v6.33 — route through the public R2-backed endpoint so website downloads
  // are byte-identical to USB-shipped files (closes the parity gap).
  const API = process.env.REACT_APP_BACKEND_URL || "";
  return `${API}/api/blm/public/${blmFilename(grade, modality, bookletType, tier, form)}`;
}

/**
 * TierRow — renders a single tier (A or B/C) with Form A/B toggle.
 * viewMode: 'print' | 'teacher' | 'demo'
 *   print   — show everything (default, print-shop dispatch)
 *   teacher — hide Student + Form A/B + Share; show Teacher only
 *   demo    — hide Teacher + Form A/B + Share; show Student only
 */
function TierRow({ tier, tierLabel, levelRange, bgClass, tierTextClass, levelTextClass, links, grade, modality, copiedLink, onShare, viewMode = 'print' }) {
  const [showForms, setShowForms] = useState(false);
  const studentKey = `student_${tier}`;
  const teacherKey = `teacher_${tier}`;

  const showStudent = viewMode !== 'teacher';
  const showTeacher = viewMode !== 'demo';
  const showShare = viewMode === 'print';
  const showFormToggle = viewMode === 'print';

  return (
    <div>
      <div className={`flex items-center justify-between py-1.5 px-3 border ${bgClass}`}>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-semibold ${tierTextClass}`}>{tierLabel} <span className={`font-normal ${levelTextClass}`}>({levelRange})</span></span>
          {showFormToggle && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setShowForms(!showForms); }}
              className={`text-[10px] px-1.5 py-0.5 rounded border transition-colors ${showForms ? 'bg-amber-100 border-amber-300 text-amber-700' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'}`}
              data-testid={`form-toggle-${grade}-${modality}-${tier}`}
            >
              {showForms ? 'Hide Forms' : 'Form A/B'}
            </button>
          )}
        </div>
        <div className="flex gap-2">
          {showStudent && (
            <DownloadButton
              url={links[studentKey]}
              label="Student"
              icon={Download}
              colorClasses="px-2.5 py-1 text-xs font-medium text-[#002FA7] bg-white hover:bg-blue-50 border border-blue-200 transition-colors"
              testId={`download-student-${grade}-${modality}-${tier}`}
            />
          )}
          {showTeacher && (
            <DownloadButton
              url={links[teacherKey]}
              label="Teacher"
              icon={BookOpen}
              colorClasses="px-2.5 py-1 text-xs font-medium text-emerald-700 bg-white hover:bg-emerald-50 border border-emerald-200 transition-colors"
              testId={`download-teacher-${grade}-${modality}-${tier}`}
            />
          )}
          {showShare && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onShare(grade, modality, 'student', tier); }}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-500 bg-white hover:bg-gray-50 border border-gray-200 transition-colors"
              data-testid={`share-${grade}-${modality}-${tier}`}
            >
              {copiedLink === `${grade}-${modality}-student-${tier}` ? <Check size={10} className="text-emerald-500" /> : <Link2 size={10} />}
              {copiedLink === `${grade}-${modality}-student-${tier}` ? 'Copied!' : 'Share'}
            </button>
          )}
        </div>
      </div>

      {/* Form A/B sub-rows — only shown in Print mode */}
      {showForms && showFormToggle && (
        <div className="ml-4 space-y-1 py-1.5">
          {['FormA', 'FormB'].map(form => (
            <div key={form} className="flex items-center justify-between py-1 px-3 bg-gray-50 border border-gray-100 text-[11px]">
              <span className="font-medium text-gray-600">{form === 'FormA' ? 'Form A' : 'Form B'} <span className="text-gray-400 font-normal">(re-administration alternate)</span></span>
              <div className="flex gap-2">
                <DownloadButton
                  url={buildUrl(grade, modality, 'student', tier, form)}
                  label="Student"
                  icon={Download}
                  colorClasses="px-2 py-0.5 text-[10px] font-medium text-[#002FA7] bg-white hover:bg-blue-50 border border-blue-200 transition-colors"
                  testId={`download-student-${grade}-${modality}-${tier}-${form}`}
                />
                <DownloadButton
                  url={buildUrl(grade, modality, 'teacher', tier, form)}
                  label="Teacher"
                  icon={BookOpen}
                  colorClasses="px-2 py-0.5 text-[10px] font-medium text-emerald-700 bg-white hover:bg-emerald-50 border border-emerald-200 transition-colors"
                  testId={`download-teacher-${grade}-${modality}-${tier}-${form}`}
                />
              </div>
            </div>
          ))}
          <p className="text-[9px] text-gray-400 px-3 italic">
            Forms A &amp; B contain different questions at equivalent difficulty for re-administration without answer memorization.
          </p>
        </div>
      )}
    </div>
  );
}

export default function PrintMaterialsTab() {
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedGrade, setExpandedGrade] = useState(null);
  const [copiedLink, setCopiedLink] = useState(null);
  // View-mode selector (v6.32.83): controls which download buttons
  // are visible. No backend change; no file regeneration. Each mode
  // still pulls PDFs from Cloudflare R2 via the existing download-token
  // path.
  //   print   — print-shop dispatch (everything visible, default)
  //   teacher — Teacher Key only (hides Student + Form A/B + Share)
  //   demo    — Tier A Student sample only (hides Teacher, Tier B/C,
  //             Form A/B, Share) — for sales/demo use without leaking
  //             the full exam corpus
  const [viewMode, setViewMode] = useState('print');

  useEffect(() => {
    axios.get(`${API}/api/blm/static-catalog`)
      .then(res => {
        setCatalog(res.data.catalog || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const handleShareLink = async (grade, modality, bookletType, tier = 'all') => {
    const key = `${grade}-${modality}-${bookletType}-${tier}`;
    try {
      const res = await axios.post(
        `${API}/api/blm/share-link?grade=${grade}&modality=${modality}&booklet_type=${bookletType}&tier=${tier}`
      );
      const fullUrl = `${API}${res.data.share_path}`;
      try {
        await navigator.clipboard.writeText(fullUrl);
      } catch {
        window.prompt('Copy this shareable link (valid 7 days):', fullUrl);
      }
      setCopiedLink(key);
      setTimeout(() => setCopiedLink(null), 3000);
    } catch (err) {
      console.error('Share link failed:', err);
      toast.error('Could not create share link', {
        description: 'Please try again.',
      });
    }
  };

  return (
    <div className="space-y-6" data-testid="print-materials-tab">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Black Line Masters
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Commercial print-ready assessment booklets (PDF/X-1a, 300 DPI grayscale, crop marks, bleed).
            Each grade pack includes 20 Student Booklets + Teacher Edition.
          </p>
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold">
          <Printer size={12} />
          Print-Shop Ready
        </div>
      </div>

      {/* View-mode selector (v6.32.83) — Print / Teacher / Demo */}
      <div className="flex items-center justify-between gap-4 bg-white border border-gray-200 px-4 py-3" data-testid="view-mode-selector">
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">View as</span>
          <div className="inline-flex border border-gray-200 overflow-hidden" role="group" aria-label="Print Materials view mode">
            {[
              { id: 'print', label: 'Print', hint: 'Print-shop dispatch — everything' },
              { id: 'teacher', label: 'Teacher', hint: 'Teacher Key only' },
              { id: 'demo', label: 'Demo', hint: 'One Tier A Student sample per modality' },
            ].map(opt => {
              const active = viewMode === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setViewMode(opt.id)}
                  className={`px-3 py-1.5 text-xs font-medium border-r border-gray-200 last:border-r-0 transition-colors ${active ? 'bg-[#002FA7] text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                  title={opt.hint}
                  data-testid={`view-mode-${opt.id}`}
                  aria-pressed={active}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="text-xs text-gray-500 text-right truncate">
          {viewMode === 'print' && <span>Full print-shop pack: Student Booklet + Teacher Key + Form A/B + Share links.</span>}
          {viewMode === 'teacher' && <span>Teacher Key only &middot; Student Booklets + Form A/B + Share hidden.</span>}
          {viewMode === 'demo' && <span>Demo preview &middot; one Tier A Student Booklet per modality &middot; Teacher + Tier B/C + Share hidden.</span>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 p-5 flex gap-4">
          <div className="flex-shrink-0 w-10 h-10 bg-blue-50 flex items-center justify-center">
            <FileText size={20} className="text-[#002FA7]" />
          </div>
          <div>
            <div className="font-semibold text-gray-900 mb-1">Student Test Booklet</div>
            <p className="text-sm text-gray-500">
              Clean, print-ready assessment with Big Picture images, questions, and answer spaces.
              20 copies included per grade pack. Includes name/date fields.
            </p>
          </div>
        </div>
        <div className="bg-white border border-gray-200 p-5 flex gap-4">
          <div className="flex-shrink-0 w-10 h-10 bg-emerald-50 flex items-center justify-center">
            <BookOpen size={20} className="text-emerald-600" />
          </div>
          <div>
            <div className="font-semibold text-gray-900 mb-1">Teacher Answer Key & Guide</div>
            <p className="text-sm text-gray-500">
              Complete answer key with ELD proficiency level mapping, tier administration guide, accommodations reference,
              anchor papers/exemplars, scoring rubrics, composite score calculator, speaking protocol,
              test security forms, and grouping guide. Form A/B available for re-administration.
            </p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading catalog...</div>
      ) : (
        <div className="space-y-3" data-testid="blm-catalog">
          {catalog.map(gradeEntry => {
            const isExpanded = expandedGrade === gradeEntry.grade;
            const colorClass = GRADE_COLORS[gradeEntry.grade] || "bg-gray-50 border-gray-200 text-gray-700";
            const totalQ = gradeEntry.modalities.reduce((sum, m) => sum + m.question_count, 0);

            return (
              <div key={gradeEntry.grade} className="bg-white border border-gray-200 overflow-hidden" data-testid={`blm-grade-${gradeEntry.grade}`}>
                <button
                  onClick={() => setExpandedGrade(isExpanded ? null : gradeEntry.grade)}
                  className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors"
                  data-testid={`blm-grade-toggle-${gradeEntry.grade}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`px-3 py-1 text-sm font-bold border ${colorClass}`}>
                      {gradeEntry.grade_label}
                    </div>
                    <span className="text-sm text-gray-500">
                      {gradeEntry.modalities.length} modalities &middot; {totalQ} questions
                    </span>
                  </div>
                  {isExpanded ? <ChevronDown size={20} className="text-gray-400" /> : <ChevronRight size={20} className="text-gray-400" />}
                </button>

                {isExpanded && (
                  <div className="border-t border-gray-100 divide-y divide-gray-50">
                    {gradeEntry.modalities.map(mod => {
                      const hasTiers = mod.tier_a_count > 0 && mod.tier_bc_count > 0;
                      const links = mod.download_links || {};

                      return (
                        <div key={mod.modality} className="px-5 py-3 pl-8 border-b border-gray-50 last:border-b-0" data-testid={`blm-mod-${gradeEntry.grade}-${mod.modality}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                              <span className="text-lg">{MODALITY_ICONS[mod.modality_label] || ""}</span>
                              <div>
                                <div className="font-medium text-gray-800">{mod.modality_label}</div>
                                <div className="text-xs text-gray-400">
                                  {mod.question_count} questions
                                  {hasTiers && <span> &middot; Tier A: {mod.tier_a_count} &middot; Tier B/C: {mod.tier_bc_count}</span>}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="ml-9 space-y-2">
                            {hasTiers ? (
                              <>
                                {/* Tier A */}
                                <TierRow
                                  tier="A"
                                  tierLabel="Tier A"
                                  levelRange="Levels 1–3"
                                  bgClass="bg-blue-50/50 border-blue-100"
                                  tierTextClass="text-blue-800"
                                  levelTextClass="text-blue-500"
                                  links={links}
                                  grade={gradeEntry.grade}
                                  modality={mod.modality}
                                  copiedLink={copiedLink}
                                  onShare={handleShareLink}
                                  viewMode={viewMode}
                                />

                                {/* Tier B/C — hidden in Demo mode (one sample per modality) */}
                                {viewMode !== 'demo' && (
                                  <TierRow
                                    tier="BC"
                                    tierLabel="Tier B/C"
                                    levelRange="Levels 3–6"
                                    bgClass="bg-violet-50/50 border-violet-100"
                                    tierTextClass="text-violet-800"
                                    levelTextClass="text-violet-500"
                                    links={links}
                                    grade={gradeEntry.grade}
                                    modality={mod.modality}
                                    copiedLink={copiedLink}
                                    onShare={handleShareLink}
                                    viewMode={viewMode}
                                  />
                                )}
                              </>
                            ) : (
                              <div className="flex items-center justify-end gap-2">
                                {viewMode !== 'teacher' && (
                                  <DownloadButton
                                    url={links.student_ALL}
                                    label="Student Booklet"
                                    icon={Download}
                                    colorClasses="px-3 py-1.5 text-xs font-medium text-[#002FA7] bg-blue-50 hover:bg-blue-100 border border-blue-200 transition-colors"
                                    testId={`download-student-${gradeEntry.grade}-${mod.modality}`}
                                  />
                                )}
                                {viewMode !== 'demo' && (
                                  <DownloadButton
                                    url={links.teacher_ALL}
                                    label="Teacher Key"
                                    icon={BookOpen}
                                    colorClasses="px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 transition-colors"
                                    testId={`download-teacher-${gradeEntry.grade}-${mod.modality}`}
                                  />
                                )}
                                {viewMode === 'print' && (
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); handleShareLink(gradeEntry.grade, mod.modality, 'student'); }}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 bg-white hover:bg-gray-50 border border-gray-200 transition-colors"
                                    data-testid={`share-${gradeEntry.grade}-${mod.modality}`}
                                  >
                                    {copiedLink === `${gradeEntry.grade}-${mod.modality}-student-all` ? <Check size={12} className="text-emerald-500" /> : <Link2 size={12} />}
                                    {copiedLink === `${gradeEntry.grade}-${mod.modality}-student-all` ? 'Copied!' : 'Share'}
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="bg-amber-50 border border-amber-200 p-6" data-testid="print-tips">
        <div className="flex items-start gap-3">
          <Printer size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-amber-800 mb-2">Print Shop Specifications</div>
            <ul className="text-sm text-amber-700 space-y-1">
              <li>PDFs are <b>PDF/X-1a</b> compliant for commercial offset and digital press</li>
              <li>All images converted to <b>300 DPI grayscale</b> for clean B&W printing</li>
              <li>Includes <b>0.125&quot; bleed</b>, <b>crop marks</b>, and <b>0.25&quot; spine gutter</b></li>
              <li><b>Tier A</b> = Proficiency Levels 1-3 &mdash; <b>Tier B/C</b> = Levels 3-6 (Level 3 overlaps both tiers)</li>
              <li>K-2 booklets use <b>large visual answer boxes</b> with pictogram illustrations</li>
              <li>Teacher Key includes Big Picture stories, scoring worksheets, and grouping guide</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
